/**
 * Phase 20 Plan 20-02 — CreateRecipientDialog
 *
 * RHF + zod. Type is locked to 'User' (Group/External deferred per D-184).
 * Schema: userId (positive int), displayName (1-200 chars), email (optional),
 *         type ('User'), isActive (bool default true).
 */

import { zodResolver } from '@hookform/resolvers/zod';
import { type JSX } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useCreateRecipientMutation } from '@/hooks/useNotificationsQueries';
import { toast } from '@/lib/toast';

const createRecipientSchema = z.object({
  userId: z.coerce
    .number({ invalid_type_error: 'User ID must be a positive integer' })
    .int()
    .positive({ message: 'User ID must be a positive integer' }),
  displayName: z.string().min(1, 'Display name is required').max(200),
  email: z.string().superRefine((v, ctx) => {
    if (v !== '' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/u.test(v)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Invalid email address' });
    }
  }),
  type: z.literal('User'),
  isActive: z.boolean().default(true),
});

type CreateRecipientFormValues = z.infer<typeof createRecipientSchema>;

interface CreateRecipientDialogProps {
  readonly open: boolean;
  readonly onClose: () => void;
}

export function CreateRecipientDialog({ open, onClose }: CreateRecipientDialogProps): JSX.Element {
  const createMutation = useCreateRecipientMutation();

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
    reset,
    setError,
  } = useForm<CreateRecipientFormValues>({
    resolver: zodResolver(createRecipientSchema),
    defaultValues: { userId: '' as unknown as number, displayName: '', email: '', type: 'User' as const, isActive: true },
    mode: 'onSubmit',
  });

  const onSubmit = async (values: CreateRecipientFormValues): Promise<void> => {
    try {
      const emailValue = values.email !== '' ? values.email : undefined;
      const body =
        emailValue !== undefined
          ? { userId: values.userId, displayName: values.displayName, email: emailValue, type: 'User' as const, isActive: values.isActive }
          : { userId: values.userId, displayName: values.displayName, type: 'User' as const, isActive: values.isActive };
      await createMutation.mutateAsync(body);
      toast.success('Recipient created');
      reset();
      onClose();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to create recipient';
      setError('displayName', { message: msg });
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent data-testid="create-recipient-dialog" className="max-w-md">
        <DialogHeader>
          <DialogTitle>Add Notification Recipient</DialogTitle>
        </DialogHeader>

        <form
          onSubmit={(e) => { void handleSubmit(onSubmit)(e); }}
          className="space-y-4"
        >
          {/* Display Name */}
          <div className="space-y-1">
            <Label htmlFor="displayName">Display Name *</Label>
            <Input
              id="displayName"
              data-testid="field-displayName"
              {...register('displayName')}
            />
            {errors.displayName !== undefined && (
              <p className="text-xs text-destructive" data-testid="error-displayName">
                {errors.displayName.message}
              </p>
            )}
          </div>

          {/* User ID */}
          <div className="space-y-1">
            <Label htmlFor="userId">User ID *</Label>
            <Input
              id="userId"
              data-testid="field-userId"
              type="number"
              min={1}
              {...register('userId')}
            />
            {errors.userId !== undefined && (
              <p className="text-xs text-destructive" data-testid="error-userId">
                {errors.userId.message}
              </p>
            )}
          </div>

          {/* Email */}
          <div className="space-y-1">
            <Label htmlFor="email">Email (optional)</Label>
            <Input
              id="email"
              data-testid="field-email"
              type="text"
              {...register('email')}
            />
            {errors.email !== undefined && (
              <p className="text-xs text-destructive" data-testid="error-email">
                {errors.email.message}
              </p>
            )}
          </div>

          {/* Type (locked) */}
          <div className="space-y-1">
            <Label htmlFor="type">Type</Label>
            <select
              id="type"
              data-testid="field-type"
              {...register('type')}
              className="w-full rounded border px-2 py-1 text-sm"
            >
              <option value="User">User</option>
              <option value="Group" disabled>Group (coming soon)</option>
              <option value="External" disabled>External (coming soon)</option>
            </select>
          </div>

          {/* Is Active */}
          <div className="flex items-center gap-2">
            <Checkbox
              id="isActive"
              data-testid="field-isActive"
              checked={watch('isActive')}
              onCheckedChange={(v) => { setValue('isActive', v === true); }}
            />
            <Label htmlFor="isActive">Active</Label>
          </div>

          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" size="sm" onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              size="sm"
              disabled={createMutation.isPending}
              data-testid="create-recipient-submit-btn"
            >
              Create Recipient
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
